// ============================================================
// AgentWorkbench — the persistent, dockable agent supervision surface. Mounted
// once in the shell; stays reachable while the owner navigates. Two modes:
//   compose → Compass recommendation for a pending instruction → Start task
//   task    → live supervision (chat/plan/activity/approvals/outputs)
//
// Uses the canonical runtime only (api.agentRuntime.*). Task truth is fetched
// from the backend; a refresh reopens the active task from server state.
// Desktop: right-docked panel. Narrow screens: full-screen sheet.
// ============================================================

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../api/client'
import { useWorkbench } from './WorkbenchContext'

type Rec = Record<string, unknown>
const s = (v: unknown, d = ''): string => (typeof v === 'string' ? v : d)
const TERMINAL = new Set(['completed', 'completed_with_warnings', 'failed', 'cancelled'])
const TABS = ['Chat', 'Plan', 'Activity', 'Approvals', 'Outputs'] as const
type Tab = typeof TABS[number]

const statusColor: Record<string, string> = {
  completed: 'var(--sc-green)', completed_with_warnings: 'var(--sc-orange)', failed: 'var(--sc-red)',
  cancelled: 'var(--sc-text-muted)', awaiting_approval: 'var(--sc-orange)', running: 'var(--sc-cyan)',
  planning: 'var(--sc-cyan)', paused: 'var(--sc-text-tertiary)', queued: 'var(--sc-text-tertiary)', blocked: 'var(--sc-red)',
}

function useNarrow() {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < 768)
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])
  return narrow
}

export default function AgentWorkbench() {
  const wb = useWorkbench()
  const navigate = useNavigate()
  const narrow = useNarrow()

  const [rec, setRec] = useState<Rec | null>(null)
  const [alts, setAlts] = useState<Rec[]>([])
  const [providerReady, setProviderReady] = useState<boolean | null>(null)
  const [chosenAgent, setChosenAgent] = useState<string>('')
  const [mode, setMode] = useState('supervised')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const [task, setTask] = useState<Rec | null>(null)
  const [events, setEvents] = useState<Rec[]>([])
  const [pending, setPending] = useState<Rec | null>(null)
  const [tab, setTab] = useState<Tab>('Chat')

  // ── compose: fetch a recommendation for the pending instruction ──
  useEffect(() => {
    if (wb.mode !== 'compose' || !wb.composeInstruction) return
    setErr(null); setRec(null); setAlts([])
    api.agentRuntime.recommend(wb.composeInstruction, (wb.composeContext ?? undefined) as Rec | undefined)
      .then((r) => {
        setRec(r.recommended); setAlts(r.alternatives); setProviderReady(r.provider_configured)
        if (r.recommended) { setChosenAgent(s(r.recommended.agent_id)); setMode(s(r.recommended.execution_mode, 'supervised')) }
      })
      .catch(() => setErr('Could not reach the router.'))
  }, [wb.mode, wb.composeInstruction, wb.composeContext])

  // ── task: fetch + poll ──
  const refresh = useCallback(async (id: string) => {
    try {
      const [t, e] = await Promise.all([api.agentRuntime.tasks.get(id), api.agentRuntime.events(id)])
      setTask(t.task); setPending(t.pending_approval); setEvents(e.events)
      const st = s((t.task as Rec).runtime_status)
      wb.setAttention(st === 'awaiting_approval' ? 'approval' : TERMINAL.has(st) ? 'none' : 'running')
    } catch { /* transient */ }
  }, [wb])

  useEffect(() => { if (wb.mode === 'task' && wb.activeTaskId) void refresh(wb.activeTaskId) }, [wb.mode, wb.activeTaskId, refresh])
  useEffect(() => {
    if (wb.mode !== 'task' || !wb.activeTaskId || !task) return
    if (TERMINAL.has(s(task.runtime_status))) return
    const h = setInterval(() => wb.activeTaskId && void refresh(wb.activeTaskId), 2500)
    return () => clearInterval(h)
  }, [wb.mode, wb.activeTaskId, task, refresh])

  const startTask = useCallback(async () => {
    if (!chosenAgent) return
    setBusy(true); setErr(null)
    try {
      const ctx = wb.composeContext
      const r = await api.agentRuntime.tasks.create({
        agent_id: chosenAgent, title: wb.composeInstruction.slice(0, 60), instruction: wb.composeInstruction,
        project_id: ctx?.project_id ?? undefined, execution_mode: mode,
      })
      wb.openTask(r.task_id)
      await api.agentRuntime.tick(r.task_id).catch(() => {})
      await refresh(r.task_id)
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not start the task.') } finally { setBusy(false) }
  }, [chosenAgent, mode, wb, refresh])

  const runTick = useCallback(async () => { if (!wb.activeTaskId) return; setBusy(true); try { await api.agentRuntime.tick(wb.activeTaskId); await refresh(wb.activeTaskId) } finally { setBusy(false) } }, [wb.activeTaskId, refresh])
  const decide = useCallback(async (d: 'approved' | 'rejected') => {
    if (!wb.activeTaskId || !pending) return
    setBusy(true)
    try { await api.agentRuntime.approve(s(pending.id), d); await api.agentRuntime.tick(wb.activeTaskId); await refresh(wb.activeTaskId) }
    catch (e) { setErr(e instanceof Error ? e.message : 'Approval failed.') } finally { setBusy(false) }
  }, [wb.activeTaskId, pending, refresh])
  const control = useCallback(async (a: 'pause' | 'resume' | 'cancel' | 'retry') => {
    if (!wb.activeTaskId) return; setBusy(true)
    try { await api.agentRuntime.control(wb.activeTaskId, a); if (a === 'resume' || a === 'retry') await api.agentRuntime.tick(wb.activeTaskId); await refresh(wb.activeTaskId) } finally { setBusy(false) }
  }, [wb.activeTaskId, refresh])

  const status = s(task?.runtime_status)
  const plan = (task?.public_plan as Rec | undefined)?.summary
  const chatEvents = useMemo(() => events.filter((e) => ['task.plan_ready', 'task.completed', 'task.failed', 'approval.requested', 'approval.granted', 'context.loaded'].includes(s(e.event_type))), [events])

  // ── minimized dock tab ──
  if (wb.mode === 'closed' && !wb.minimized) return null
  if (wb.minimized) {
    return (
      <button onClick={wb.restore} aria-label="Reopen agent workbench" style={dockTab(wb.attention)}>
        <span aria-hidden>◆</span> Agent {wb.attention === 'approval' ? '· approval needed' : wb.attention === 'running' ? '· running' : ''}
      </button>
    )
  }

  const shell: React.CSSProperties = narrow
    ? { position: 'fixed', inset: 0, zIndex: 60 }
    : { position: 'fixed', top: 64, right: 0, bottom: 0, width: 480, zIndex: 60 }

  return (
    <aside role="complementary" aria-label="Agent Workbench" style={{ ...shell, background: 'var(--sc-surface-1)', borderLeft: '1px solid var(--sc-border-rest)', color: 'var(--sc-text-primary)', fontFamily: 'var(--sc-font-ui)', display: 'flex', flexDirection: 'column', boxShadow: '-20px 0 60px rgba(0,0,0,0.4)' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid var(--sc-border-rest)' }}>
        <div>
          <strong style={{ fontFamily: 'var(--sc-font-display)' }}>Agent Workbench</strong>
          {wb.mode === 'task' && <span style={{ marginLeft: 8, fontSize: 11, color: statusColor[status] ?? 'var(--sc-text-secondary)', fontFamily: 'var(--sc-font-mono)' }}>{status}</span>}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={wb.minimize} aria-label="Minimize" style={iconBtn}>—</button>
          <button onClick={wb.close} aria-label="Close (does not cancel the task)" style={iconBtn}>×</button>
        </div>
      </div>

      {/* COMPOSE */}
      {wb.mode === 'compose' && (
        <div style={{ padding: 14, overflowY: 'auto', display: 'grid', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--sc-text-secondary)' }}>"{wb.composeInstruction}"</div>
          {wb.composeContext?.project_id && <div style={chip}>Project: {wb.composeContext.project_id}</div>}
          {providerReady === false && <div style={{ ...card, borderColor: 'var(--sc-orange)' }}><span style={{ color: 'var(--sc-orange)' }}>Model not configured</span> — the agent will plan and gate writes, but can't make live calls until <code>ANTHROPIC_API_KEY</code> is set.</div>}
          {err && <div style={{ color: 'var(--sc-red)', fontSize: 12 }}>{err}</div>}
          {!rec && !err && <div style={{ color: 'var(--sc-text-tertiary)', fontSize: 13 }}>Finding the best agent…</div>}
          {rec && (
            <>
              <div style={{ fontSize: 12, color: 'var(--sc-text-tertiary)' }}>Recommended</div>
              {[rec, ...alts].map((a) => {
                const id = s(a.agent_id); const on = id === chosenAgent
                return (
                  <button key={id} onClick={() => setChosenAgent(id)} style={{ ...card, textAlign: 'left', cursor: 'pointer', borderColor: on ? 'var(--sc-border-active)' : 'var(--sc-border-rest)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <strong>{s(a.display_name, id)}</strong>
                      <span style={{ fontSize: 11, color: 'var(--sc-text-tertiary)' }}>{s(a.role)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--sc-text-secondary)', marginTop: 3 }}>{s(a.reason)}</div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                      {(a.skills as Rec[] ?? []).slice(0, 4).map((sk) => (
                        <span key={s(sk.skill_id)} style={{ ...miniChip, borderColor: s(sk.state) === 'live' ? 'var(--sc-green)' : s(sk.state) === 'configuration_missing' ? 'var(--sc-orange)' : 'var(--sc-border-hover)' }}>{s(sk.display_name)}</span>
                      ))}
                    </div>
                  </button>
                )
              })}
              <label style={{ fontSize: 12, color: 'var(--sc-text-secondary)' }}>Execution mode
                <select value={mode} onChange={(e) => setMode(e.target.value)} style={input}>
                  <option value="advise">Advise (read-only)</option>
                  <option value="supervised">Supervised (writes need approval)</option>
                  <option value="draft">Draft only</option>
                </select>
              </label>
              <button onClick={startTask} disabled={busy || !chosenAgent} style={primaryBtn(busy || !chosenAgent)}>{busy ? 'Starting…' : 'Start task'}</button>
            </>
          )}
        </div>
      )}

      {/* TASK */}
      {wb.mode === 'task' && (
        <>
          <div role="tablist" style={{ display: 'flex', gap: 2, padding: '8px 10px', borderBottom: '1px solid var(--sc-border-rest)' }}>
            {TABS.map((t) => (
              <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} style={{ ...tabBtn, color: tab === t ? 'var(--sc-text-primary)' : 'var(--sc-text-tertiary)', borderBottom: tab === t ? '2px solid var(--sc-cyan)' : '2px solid transparent' }}>{t}{t === 'Approvals' && pending ? ' •' : ''}</button>
            ))}
          </div>
          <div aria-live="polite" style={{ padding: 14, overflowY: 'auto', flex: 1, display: 'grid', gap: 10, alignContent: 'start' }}>
            {tab === 'Chat' && (
              <>
                {chatEvents.map((e) => <div key={s(e.id)} style={{ fontSize: 13, color: 'var(--sc-text-secondary)' }}>{s(e.message)}</div>)}
                {s(task?.final_summary) && <div style={{ ...card, borderColor: 'var(--sc-green)' }}><strong>Result</strong><div style={{ marginTop: 4 }}>{s(task?.final_summary)}</div></div>}
              </>
            )}
            {tab === 'Plan' && <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', color: 'var(--sc-text-secondary)' }}>{plan != null ? s(plan) : 'No plan yet.'}</div>}
            {tab === 'Activity' && events.map((e) => (
              <div key={s(e.id)} style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 8, fontSize: 12, borderBottom: '1px solid var(--sc-border-rest)', paddingBottom: 5 }}>
                <span style={{ fontFamily: 'var(--sc-font-mono)', color: 'var(--sc-text-tertiary)' }}>{s(e.event_type)}</span>
                <span style={{ color: 'var(--sc-text-secondary)' }}>{s(e.message)}</span>
              </div>
            ))}
            {tab === 'Approvals' && (pending ? (
              <div style={{ ...card, borderColor: 'var(--sc-orange)' }}>
                <strong style={{ color: 'var(--sc-orange)' }}>Approval required</strong>
                <div style={{ fontSize: 13, margin: '6px 0' }}>{s(pending.summary)}</div>
                <pre style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', background: 'var(--sc-surface-2)', padding: 8, borderRadius: 6, overflow: 'auto' }}>{JSON.stringify(pending.proposed_payload ?? {}, null, 2)}</pre>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => decide('approved')} disabled={busy} style={primaryBtn(busy)}>Approve</button>
                  <button onClick={() => decide('rejected')} disabled={busy} style={ghostBtn}>Reject</button>
                </div>
              </div>
            ) : <div style={{ color: 'var(--sc-text-tertiary)', fontSize: 13 }}>No pending approvals.</div>)}
            {tab === 'Outputs' && (
              <>
                {s(task?.final_summary) ? <div style={{ fontSize: 13 }}>{s(task?.final_summary)}</div> : <div style={{ color: 'var(--sc-text-tertiary)', fontSize: 13 }}>Outputs appear here as the agent produces them.</div>}
                {wb.composeContext?.project_id && <button onClick={() => navigate(`/square/command-center/projects/${wb.composeContext!.project_id}`)} style={ghostBtn}>Open project</button>}
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '10px 14px', borderTop: '1px solid var(--sc-border-rest)' }}>
            {!TERMINAL.has(status) && <button onClick={runTick} disabled={busy} style={ghostBtn}>Continue</button>}
            {!TERMINAL.has(status) && status !== 'paused' && <button onClick={() => control('pause')} disabled={busy} style={ghostBtn}>Pause</button>}
            {status === 'paused' && <button onClick={() => control('resume')} disabled={busy} style={ghostBtn}>Resume</button>}
            {!TERMINAL.has(status) && <button onClick={() => control('cancel')} disabled={busy} style={ghostBtn}>Cancel</button>}
            {(status === 'failed' || status === 'blocked' || status === 'completed_with_warnings') && <button onClick={() => control('retry')} disabled={busy} style={ghostBtn}>Retry</button>}
            <button onClick={() => navigate(`/square/command-center/assistants/tasks/${wb.activeTaskId}`)} style={ghostBtn}>Open full task</button>
          </div>
        </>
      )}
    </aside>
  )
}

const card: React.CSSProperties = { background: 'var(--sc-surface-2)', border: '1px solid var(--sc-border-rest)', borderRadius: 'var(--sc-radius-md)', padding: 12, fontSize: 13 }
const chip: React.CSSProperties = { fontSize: 11, color: 'var(--sc-text-secondary)', border: '1px solid var(--sc-border-rest)', borderRadius: 999, padding: '2px 10px', width: 'fit-content' }
const miniChip: React.CSSProperties = { fontSize: 10, color: 'var(--sc-text-secondary)', border: '1px solid var(--sc-border-hover)', borderRadius: 6, padding: '2px 6px' }
const input: React.CSSProperties = { width: '100%', marginTop: 4, padding: '8px 10px', background: 'var(--sc-surface-2)', border: '1px solid var(--sc-border-rest)', borderRadius: 6, color: 'var(--sc-text-primary)', fontSize: 13, boxSizing: 'border-box' }
const iconBtn: React.CSSProperties = { width: 28, height: 28, background: 'transparent', color: 'var(--sc-text-secondary)', border: '1px solid var(--sc-border-hover)', borderRadius: 6, cursor: 'pointer' }
const tabBtn: React.CSSProperties = { padding: '6px 10px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12 }
function primaryBtn(disabled: boolean): React.CSSProperties { return { padding: '9px 14px', background: disabled ? 'var(--sc-surface-4)' : 'var(--sc-cyan)', color: disabled ? 'var(--sc-text-tertiary)' : '#05070f', border: 'none', borderRadius: 6, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13 } }
const ghostBtn: React.CSSProperties = { padding: '7px 11px', background: 'transparent', color: 'var(--sc-text-primary)', border: '1px solid var(--sc-border-hover)', borderRadius: 6, cursor: 'pointer', fontSize: 12 }
function dockTab(attention: string): React.CSSProperties {
  return { position: 'fixed', right: 16, bottom: 16, zIndex: 60, padding: '10px 16px', background: 'var(--sc-surface-3)', color: 'var(--sc-text-primary)', border: `1px solid ${attention === 'approval' ? 'var(--sc-orange)' : 'var(--sc-border-active)'}`, borderRadius: 999, cursor: 'pointer', fontSize: 13, boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }
}
